import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, List } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  TopPerformersCard, AlertsCard, DrillCard,
} from '../../../components/LevelDashboard';
import {
  useGetRegionalHolisticAnalyticsQuery,
  getFoundationSchoolsFromRecords,
  getFoundationRegionSummary,
  parseTermFilter,
  termFilterToQueryValue,
  mergeRegionalAnalyticsResponses,
} from '../../../services/holisticApi';
import { FoundationKPIHero } from '../components/FoundationKPIHero';
import { FoundationMasterTabs } from '../components/FoundationMasterTabs';
import KVSLoader from '../../../../../components/loaders/KVSLoader';

const BASE = '/kvs-samagam/student-analytics';

const RegionDrillDown: React.FC = () => {
  const { regionId } = useParams<{ regionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  const term = parseTermFilter(searchParams.get('term'));
  const querySuffix = `?year=${academicYear}&term=${termFilterToQueryValue(term)}`;
  const isOverall = term === 'overall';
  const termValue = termFilterToQueryValue(term);

  const numericRegionId = Number(regionId);
  const singleTermQuery = useGetRegionalHolisticAnalyticsQuery(
    !isOverall ? { regionId: numericRegionId, academicYear, term } : skipToken,
  );
  const term1Query = useGetRegionalHolisticAnalyticsQuery(
    isOverall ? { regionId: numericRegionId, academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetRegionalHolisticAnalyticsQuery(
    isOverall ? { regionId: numericRegionId, academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeRegionalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;
  const records = effectiveData?.data || [];
  const region = getFoundationRegionSummary(records, numericRegionId, term);
  const schools = getFoundationSchoolsFromRecords(records, term);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  if (!records.length) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Region not found.</p>
        <button onClick={() => navigate('..')} className="text-indigo-600 text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('..')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{region.regionName} Region</h1>
          <p className="text-sm text-gray-500">State: {region.stateCode} &mdash; {region.metrics.totalStudents.toLocaleString()} students &mdash; {schools.length} schools</p>
        </div>
        <div className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
          <select
            id="term"
            value={termValue}
            onChange={(e) => navigate(`${location.pathname}?year=${academicYear}&term=${e.target.value}`)}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="overall">Overall</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
          </select>
        </div>
      </div>

      <FoundationKPIHero
        records={records}
        term={term}
        contextLabel={`${schools.length} schools · ${region.metrics.totalStudents.toLocaleString()} students`}
        year={academicYear}
        roid={numericRegionId}
        preferCounts
      />

      <FoundationMasterTabs records={records} term={term} year={academicYear} roid={numericRegionId} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersCard items={region.topPerformers} />
        <AlertsCard
          items={region.alerts}
          onViewAll={() => navigate(`${BASE}/region/${regionId}/list/schools/below-average${querySuffix}`)}
          viewAllLabel="Need Attention Schools"
        />
      </div>

      {/* School Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Schools — Drill Down</h2>
          <button
            onClick={() => navigate(`${BASE}/region/${regionId}/list/schools${querySuffix}`)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition"
          >
            <List size={16} /> See All Schools
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {schools.map(s => (
            <DrillCard
              key={s.schoolId}
              title={s.schoolName}
              subtitle={s.location}
              metrics={s.metrics}
              alerts={s.alerts}
              navigateTo={`school/${s.schoolId}${querySuffix}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RegionDrillDown;
